import * as React from "react";
import {useTheme} from "@mui/material/styles";
import MDBox from "components/MDBox";
import {DataGrid, useGridApiRef, GridToolbar} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  AppBar,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Box,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  LastPage,
  FirstPage,
  NavigateBefore,
  NavigateNext
} from "@mui/icons-material";
import {GET} from "APIclient";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import Paper from "@mui/material/Paper";
import * as XLSX from "xlsx";
import {formatDesignation, formatDate} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";


function CustomPagination({ 
  page, 
  pageSize, 
  totalRows, 
  totalPages, 
  onPageChange, 
  onGoToLastPage,
  loading 
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        borderTop: '1px solid #e0e0e0'
      }}
    >
      <Typography variant="caption" sx={{ color: '#666' }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : 'No rows'}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: '#666' }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>
        
        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: properties?.colors?.bgcolorbluethem || '#1565c0',
              },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function ProjectReport() {
  const theme = useTheme();
  const apiRef = useGridApiRef();

  const [empId, setEmpId] = React.useState("");
  const [empName, setEmployeeName] = React.useState("");
  const [projectName, setProjectName] = React.useState("");
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [projectOptions, setProjectOptions] = React.useState([]);

  // Fetch project dropdown list
  const fetchProjectNames = async () => {
    try {
      const response = await GET("admin/project/index");
      const names = response?.data?.data?.paginatedData || [];
      setProjectOptions(names);
    } catch (error) {
      console.error("Error fetching project names:", error);
    }
  };


  const fetchData = async (page, pageSize, empId, empName, projectName) => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString()
      });

      if (empId) queryParams.append("empId", empId);
      if (empName) queryParams.append("empName", empName);
      if (projectName) queryParams.append("projectName", projectName);

      const response = await GET(`admin/assign-project-reports?${queryParams.toString()}`);

      if (response?.status === 200) {
        const responseData = response?.data?.data?.paginatedData || [];
        const formatted = responseData.map((item, index) => ({
          ...item,
          id: page * pageSize + index + 1,
          designation: formatDesignation(item?.designation)
        }));

        setData(formatted);
        setTotalRows(response?.data?.data?.totalItems || 0);
        setTotalPages(response?.data?.data?.totalPages || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData(page, pageSize, empId, empName, projectName);
    fetchProjectNames();
  }, [page, pageSize]);

  
  const handleSearch = () => {
    setPage(0);
    fetchData(0, pageSize, empId, empName, projectName);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };


  const columns = [
    {field: "id", headerName: "#", width: 50, align: "left", headerAlign: "left"},
    {field: "empId", headerName: "EMP ID", width: 80, align: "left", headerAlign: "left"},
    {field: "empName", headerName: "Employee Name", width: 180, align: "left", headerAlign: "left"},
    {
      field: "designation",
      headerName: "Designation",
      width: 220,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "projectName",
      headerName: "Project Name",
      width: 220,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 150,
      align: "left",
      headerAlign: "left",
      renderCell: params => {
        const formattedDate = formatDate(params.value);
        return formattedDate || " - ";
      }
    }
  ];


  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reporting List");
    XLSX.writeFile(workbook, "AssignProject_report.xlsx");
  };


  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage, pageSize, empId, empName, projectName);
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPage = totalPages - 1;
      setPage(lastPage);
      await fetchData(lastPage, pageSize, empId, empName, projectName);
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2}}>
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Assign Project Report
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={empId}
                  onChange={(e) => setEmpId(e.target.value)}
                  onKeyDown={handleKeyDown}
                  name="empId"
                  label="Employee ID"
                  variant="outlined"
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={empName}
                  onChange={(e) => setEmployeeName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  name="empName"
                  label="Employee Name"
                  variant="outlined"
                />
                <FormControl variant="outlined" sx={{m: 0, width: "20ch", gridColumn: "span 2"}}>
                  <InputLabel id="projectName">Project Name</InputLabel>
                  <Select
                    labelId="projectName"
                    id="projectName"
                    name="projectName"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    label="Project Name"
                    onKeyDown={handleKeyDown}
                    sx={{minWidth: 150, p: 1.5}}
                  >
                    <MenuItem value="">None</MenuItem>
                    {projectOptions.map(project => (
                      <MenuItem key={project.projectId} value={project.projectName}>
                        {project.projectName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <MDButton
                  sx={{gridColumn: "span 2", background: properties.colors.bgcolorbluethem}}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
                <MDButton
                  sx={{gridColumn: "span 2"}}
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadExcel}
                >
                  Download
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
        <MDBox>
          <Paper>
            <DataGrid
              apiRef={apiRef}
              rows={data}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSize={pageSize}
              page={page}
              onPageChange={(newPage) => handlePageChange(newPage)}
              loading={loading}
              components={{Toolbar: GridToolbar}}
              hideFooter
              sx={{
                height: "450px",
                fontFamily: properties.fontFamily,
                fontWeight:400,
                fontSize: "14px",
                "& .MuiDataGrid-cell": {
                  textAlign: "left"
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  textAlign: "left"
                },
                "& .MuiDataGrid-columnHeader": {
                  textAlign: "left"
                },
                "& .MuiDataGrid-footerContainer": {
                  justifyContent: "flex-start"
                },
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important",
                },
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
